/**
 * ModeIdentityStrip — canvas-header strip showing active mode identity.
 *
 * Sprint-2 Task 2.3 (sprint.md:233). Renders above the module canvas
 * (NOT inside TopChrome — SDD §5.1 correction). Structure:
 *
 *   {glyph} {MODE.label} · MODE · {mode.tagline}   [LOCK]  [RESET PRESET]
 *   {N} modules · {M} investigations
 *
 * LOCK/LOCKED + RESET PRESET are ghost buttons — optional callbacks;
 * when omitted the buttons hide. Reset preset is per-mode only (not
 * cross-mode) per sprint-3 AC-3.
 */

import { getModeDef, type WorkspaceModeId } from './modeRegistry';

export interface ModeIdentityStripProps {
  mode: WorkspaceModeId;
  moduleCount: number;
  investigationCount: number;
  locked?: boolean;
  onLockToggle?: () => void;
  onResetPreset?: () => void;
  className?: string;
}

export function ModeIdentityStrip({
  mode,
  moduleCount,
  investigationCount,
  locked = false,
  onLockToggle,
  onResetPreset,
  className,
}: ModeIdentityStripProps) {
  const def = getModeDef(mode);
  if (!def) return null;

  return (
    <div
      role="region"
      aria-label={`${def.label} mode identity`}
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '8px 16px',
        borderBottom: '1px solid var(--cd-border)',
        background: 'var(--cd-bg-raised)',
        fontFamily: 'var(--cd-font-sans)',
      }}
    >
      {/* Left: identity line + counts */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontFamily: 'var(--cd-font-mono)',
            fontSize: 11,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--cd-fg-secondary)',
          }}
        >
          <span
            aria-hidden="true"
            style={{ color: `var(${def.accentVar})`, fontSize: 14, lineHeight: 1 }}
          >
            {def.glyph}
          </span>
          <span style={{ color: 'var(--cd-fg)', fontWeight: 500 }}>{def.label}</span>
          <span aria-hidden="true" style={{ color: 'var(--cd-fg-muted)' }}>·</span>
          <span style={{ color: 'var(--cd-fg-muted)' }}>MODE</span>
          <span aria-hidden="true" style={{ color: 'var(--cd-fg-muted)' }}>·</span>
          <span style={{ color: 'var(--cd-fg-secondary)', fontFamily: 'var(--cd-font-sans)', textTransform: 'none', letterSpacing: 0 }}>
            {def.tagline}
          </span>
        </div>
        <div
          style={{
            fontFamily: 'var(--cd-font-mono)',
            fontSize: 10,
            color: 'var(--cd-fg-muted)',
            letterSpacing: '0.06em',
          }}
        >
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{moduleCount}</span> modules
          <span aria-hidden="true"> · </span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{investigationCount}</span> investigations
        </div>
      </div>

      {/* Right: ghost buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {onLockToggle && (
          <GhostButton
            onClick={onLockToggle}
            aria-pressed={locked}
            aria-label={locked ? 'Unlock preset' : 'Lock preset'}
          >
            {locked ? 'Locked' : 'Lock'}
          </GhostButton>
        )}
        {onResetPreset && (
          <GhostButton onClick={onResetPreset} aria-label="Reset preset">
            Reset preset
          </GhostButton>
        )}
      </div>
    </div>
  );
}

// ── Ghost button ────────────────────────────────────────────────────────

interface GhostButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  'aria-label'?: string;
  'aria-pressed'?: boolean;
}

function GhostButton({ onClick, children, ...aria }: GhostButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...aria}
      style={{
        fontFamily: 'var(--cd-font-mono)',
        fontSize: 10,
        letterSpacing: '0.10em',
        textTransform: 'uppercase',
        fontWeight: 500,
        color: 'var(--cd-fg-muted)',
        background: 'transparent',
        border: '1px solid var(--cd-border)',
        borderRadius: 'var(--cd-radius)',
        padding: '4px 10px',
        cursor: 'pointer',
        transition: 'color .12s, background .12s, border-color .12s',
      }}
    >
      {children}
    </button>
  );
}
